import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useState } from 'react';

const DataTable = ({ columns, data, title }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedData = [...(data || [])].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];
    
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const formatCellValue = (value, column) => {
    if (value === null || value === undefined) return '—';
    
    if (column.type === 'currency') {
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
    if (column.type === 'percentage') {
      return `${value.toFixed(1)}%`;
    }
    if (column.type === 'number') {
      return value.toLocaleString();
    }
    if (column.type === 'status') {
      const statusStyles = {
        green: 'bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46]',
        yellow: 'bg-[#FFFBEB] border border-[#FDE68A] text-[#92400E]',
        red: 'bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B]',
        no_plan: 'bg-[#F3F4F6] border border-[#E5E7EB] text-[#6B7280]',
        open: 'bg-[#EFF6FF] border border-[#BFDBFE] text-[#1E40AF]',
        in_progress: 'bg-[#FFFBEB] border border-[#FDE68A] text-[#92400E]',
        complete: 'bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46]'
      };
      return (
        <span className={cn(
          "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize",
          statusStyles[value] || 'bg-[#F3F4F6] border border-[#E5E7EB] text-[#6B7280]'
        )}>
          {value.replace('_', ' ')}
        </span>
      );
    }
    if (column.type === 'date') {
      return new Date(value).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    }
    return value;
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        {title && (
          <h3 className="text-sm font-medium text-[#6B7280] uppercase tracking-wider mb-4">
            {title}
          </h3>
        )}
        <div className="text-center py-8 text-[#9CA3AF]">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden" data-testid="data-table">
      {title && (
        <div className="px-6 py-4 border-b border-[#E5E7EB]">
          <h3 className="text-sm font-medium text-[#6B7280] uppercase tracking-wider">
            {title}
          </h3>
        </div>
      )}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={cn(
                    "h-10 px-4 text-xs font-medium text-[#6B7280] uppercase tracking-wider",
                    column.sortable && "cursor-pointer select-none hover:text-[#374151]"
                  )}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center gap-1">
                    {column.label}
                    {column.sortable && sortConfig.key === column.key && (
                      sortConfig.direction === 'asc' 
                        ? <ChevronUp className="w-3 h-3" />
                        : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map((row, rowIdx) => (
              <TableRow 
                key={row.id || rowIdx} 
                className="data-row border-b border-[#E5E7EB] hover:bg-[#F3F4F6]"
              >
                {columns.map((column) => (
                  <TableCell 
                    key={column.key}
                    className="px-4 py-3 text-sm text-[#111827]"
                  >
                    {formatCellValue(row[column.key], column)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default DataTable;
